import React from 'react';
import { Zap, BarChart3, Trophy, Users } from 'lucide-react';
import FeaturePage from './FeaturePage';

export default function LiveMatches() {
  return (
    <FeaturePage
      seo={{
        title: 'Live Match Simulation - Watch Football Matches Online | My Football Game',
        description: 'Watch live simulated football matches with real-time goals, cards, injuries and substitutions. Challenge friends and rivals in My Football Game\'s match engine.',
        path: '/features/live-matches',
      }}
      eyebrow="Feature Spotlight"
      title="LIVE MATCH "
      titleAccent="SIMULATION"
      subtitle="Watch your matches unfold in real time — goals, cards, injuries, momentum swings and substitutions streamed live to your screen."
      breadcrumbLabel="Live Matches"
      sections={[
        {
          icon: <Zap className="w-5 h-5" />,
          title: 'Real-Time Match Engine',
          content: (
            <>
              <p>Every match in My Football Game is processed by a detailed simulation engine that calculates events minute by minute. Player attributes, team tactics, formation matchups, home advantage and form all influence the outcome.</p>
              <p>Matches stream live via Server-Sent Events (SSE), so you see each goal, card, injury, and substitution as it happens — just like following a real match on a live ticker.</p>
            </>
          ),
        },
        {
          icon: <BarChart3 className="w-5 h-5" />,
          title: 'Detailed Match Stats',
          content: (
            <>
              <p>After every match you get a full statistical breakdown: possession, shots, shots on target, corners, fouls, cards, pass accuracy and individual player ratings. Use these stats to identify what worked and what needs adjusting.</p>
              <p>Player ratings range from 1 to 10 and reflect their individual contribution. A striker who scores a hat-trick will earn a high rating, but so will a defender who makes crucial tackles and keeps a clean sheet.</p>
            </>
          ),
        },
        {
          icon: <Users className="w-5 h-5" />,
          title: 'Challenge Anyone',
          content: (
            <>
              <p>Send challenges to friends or random opponents from your league tier. You get three free challenges per day, with the option to buy extra challenge slots using coins or gold. Every challenge is a competitive match that counts towards your league record.</p>
              <p>Random matchmaking pairs you with a manager at a similar level, ensuring fair competition. Friend challenges let you settle bragging rights directly — challenge your mates and prove who is the better manager.</p>
            </>
          ),
        },
        {
          icon: <Trophy className="w-5 h-5" />,
          title: 'Tournaments & Cups',
          content: (
            <>
              <p>Weekly knockout tournaments pit 8 teams against each other in quarter-final, semi-final, and final rounds. Enter with coins or gold for a chance to win prize pools that dwarf regular match rewards.</p>
              <p>Tournament matches use the same engine as regular challenges, but the stakes are higher — a single defeat eliminates you. Build a deep squad to handle the pressure of tournament football.</p>
            </>
          ),
        },
      ]}
      faqItems={[
        { q: 'How long does a match take?', a: 'Matches are simulated in about 2-3 minutes. Events stream live so you can watch in real time, or check the full result and stats afterwards.' },
        { q: 'Can I watch my match live?', a: 'Yes. Once a challenge is accepted and the match kicks off, you receive a live stream of every event — goals, cards, injuries, substitutions — as it happens.' },
        { q: 'How many challenges do I get per day?', a: 'You get 3 free challenges per day. Extra challenge slots can be purchased with coins or gold coins from the store.' },
        { q: 'Do match results affect my league position?', a: 'Yes. Every competitive match updates your coach record (wins, draws, losses) and your position on the global leaderboard.' },
      ]}
      ctaTitle="Watch your first match live"
      ctaText="Create your club, challenge an opponent and watch the match engine bring your tactics to life."
    />
  );
}
